import moment from 'moment';

import last from 'lodash/last';
import forOwn from 'lodash/forOwn';
import cloneDeep from 'lodash/cloneDeep';


import {
  ISchedule,
  IBakeryScheduleDay,
  IOutletScheduleDay,
  IFormattedBakerySchedule,
  IFormattedOutletSchedule,
  IFormattedDeviatingDeadline,
} from './schedule.models';
import { dateHelper } from './date-helper';

import { IClosedDay } from '../models/i-closed-day';
import { IDeviatingDeadline } from '../models/i-bakery';
import { INonWorkingRange } from '../models/i-non-working-range';
import { E_WEEK_DAY_KEYS_TO_INDEX_MAP, ISO_WEEKDAY_MAP } from '../constants/common.constants';


function getDaysBeforeOrderIncludingDayOffs(schedule: ISchedule<IBakeryScheduleDay>, dayIndex: number) {
  function getPreviousDayIndex(dayIndex) {
    dayIndex--;
    if (dayIndex < 0) {
      dayIndex = 6;
    }

    return dayIndex;
  }

  const dayKey = E_WEEK_DAY_KEYS_TO_INDEX_MAP[dayIndex];

  let prevDayIndex = getPreviousDayIndex(dayIndex);
  let workingDaysCounter = schedule[dayKey].daysBeforeOrder;
  let numberOfNonWorkingDays = 0;

  let numberOfIterations = 0;

  while (workingDaysCounter > 0) {
    numberOfIterations++;
    const prevDayKey = E_WEEK_DAY_KEYS_TO_INDEX_MAP[prevDayIndex];
    const isWorkingDay = !schedule[prevDayKey].dayOff;

    if (!isWorkingDay) {
      numberOfNonWorkingDays++;
    } else {
      workingDaysCounter--;
    }

    prevDayIndex = getPreviousDayIndex(prevDayIndex);

    if (numberOfIterations > 14) {
      break;
    }
  }

  return schedule[dayKey].daysBeforeOrder + numberOfNonWorkingDays;
}

export function getBakeryDeadlineRules(
    schedule: ISchedule<IBakeryScheduleDay>,
    unavailableDays: string[] = [],
): IFormattedBakerySchedule[] {
  if (!schedule) {
    return [];
  }

  const clonedSchedule = cloneDeep(schedule);

  const keyIndexes = [1, 2, 3, 4, 5, 6, 0];

  // Shift days according to bakery deadlines
  keyIndexes
    .forEach((keyIndex) => {
      const dayKey = E_WEEK_DAY_KEYS_TO_INDEX_MAP[keyIndex];
      clonedSchedule[dayKey].daysBeforeOrder = getDaysBeforeOrderIncludingDayOffs(clonedSchedule, keyIndex);
    });

  // collect detailed day description
  return keyIndexes.map(function (keyIndex) {
    const dayKey = E_WEEK_DAY_KEYS_TO_INDEX_MAP[keyIndex];
    const orderOnKey = clonedSchedule[dayKey].daysBeforeOrder < 7
      ? _getDayIndexToOrder(dayKey, clonedSchedule)
      : null;

    const isAvailableDay = Boolean(!unavailableDays.some(day => day.toLowerCase() === dayKey));

    return {
      targetKey: dayKey,
      orderOnKey: orderOnKey,
      orderBefore: clonedSchedule[dayKey].orderBefore,
      daysDiff: clonedSchedule[dayKey].daysBeforeOrder,
      hasPickupOrDelivery: clonedSchedule[dayKey].hasPickupOrDelivery && isAvailableDay,
    };
  })
    // collapse days
    .reduce(function (rules, day, index, days) {
      if (index === 0) {
        rules.push([day, day]);
        return rules;
      }

      // if prev day has different settings
      if (days[index - 1].daysDiff !== day.daysDiff
        || days[index - 1].orderBefore !== day.orderBefore
        || days[index - 1].hasPickupOrDelivery !== day.hasPickupOrDelivery) {
        rules.push([day, day]);
      }
      // otherwise update end with current day
      else {
        last(rules)[1] = day;
      }

      return rules;
    }, [])
    // format rules
    .map(_formatBakeryDeadlineRule);
}

function getDeviatingDeadlines(schedule: ISchedule<IBakeryScheduleDay>,
                               deviatingOrderDeadlines: IDeviatingDeadline[],
                               nonWorkingDays: IClosedDay[] = []): IFormattedDeviatingDeadline[] {
  const dayOffMap = _getDayOffMap(schedule);
  const nonWorkingRanges: INonWorkingRange[] = nonWorkingDays.map(({ begin, end }) => ({
    start: moment(begin, 'DD.MM.YYYY').startOf('day'),
    end: moment(end, 'DD.MM.YYYY').startOf('day'),
  }));
  const totalClosedDays = nonWorkingRanges.reduce(
    (sum, { start, end }) => sum + end.diff(start, 'days') + 1, 0,
  );

  return deviatingOrderDeadlines.map(({ date, daysBeforeOrder, orderBefore }) => {
    const orderDate = moment(date, 'YYYY-MM-DD');

    return {
      orderDate: orderDate.format('D. MMMM'),
      beforeTime: moment(dateHelper.convertMinutesCountToTime(orderBefore)).format('HH:mm'),
      beforeDate: _getDeviatingBeforeDate(orderDate, daysBeforeOrder, dayOffMap, nonWorkingRanges, totalClosedDays)
    };
  });
}

function _getDeviatingBeforeDate(orderDate: moment.Moment,
                                 daysBeforeOrder: number,
                                 dayOffMap: { [key: number]: boolean },
                                 nonWorkingRanges: INonWorkingRange[],
                                 totalClosedDays: number): string {
  // Worst case: every working week-day requires skipping all 6 other weekdays as day-offs,
  // plus we may walk through the entire closed-day set.
  const maxIterations = daysBeforeOrder * 7 + totalClosedDays;

  const cursor = orderDate.clone();
  let workingDaysLeft = daysBeforeOrder;

  for (let i = 0; workingDaysLeft > 0 && i < maxIterations; i++) {
    cursor.subtract(1, 'day');

    const isNonWorking = !!dayOffMap[cursor.isoWeekday()]
      || _isDateInNonWorkingRange(cursor, nonWorkingRanges);

    if (!isNonWorking) {
      workingDaysLeft--;
    }
  }

  return cursor.format('D. MMMM');
}

function _getDayOffMap(schedule: ISchedule<IBakeryScheduleDay>): { [key: number]: boolean } {
  const dayOffMap = {};

  forOwn(schedule, ({ dayOff }, weekDay) => {
    const weekDayIndex = ISO_WEEKDAY_MAP[weekDay];
    dayOffMap[weekDayIndex] = dayOff;
  });

  return dayOffMap;
}

function _isDateInNonWorkingRange(date: moment.Moment, nonWorkingRanges: INonWorkingRange[]): boolean {
  return nonWorkingRanges.some(({ start, end }) => date.isBetween(start, end, null, '[]'));
}

export function getOutletSchedule(schedule: ISchedule<IOutletScheduleDay>): IFormattedOutletSchedule[] {
  if (!schedule) {
    return [];
  }

  const keyIndexes = [1, 2, 3, 4, 5, 6, 0];

  return keyIndexes
    // filter out day off
    // collect detailed day description
    .map(function (keyIndex) {
      const dayKey = E_WEEK_DAY_KEYS_TO_INDEX_MAP[keyIndex];
      return {
        targetKey: dayKey,
        info: schedule[dayKey] || { dayOff: true }
      };
    })
    // collapse days
    .reduce(function (rules, day, index, days) {
      if (index === 0) {
        rules.push([day, day]);
        return rules;
      }

      let prevDay = days[index - 1];
      let { info: prevInfo } = prevDay;
      let { info } = day;
      // if prev day has different settings
      if (prevInfo.dayOff !== info.dayOff ||
        prevInfo.openingHoursStart !== info.openingHoursStart ||
        prevInfo.openingHoursEnd !== info.openingHoursEnd) {
        rules.push([day, day]);
      }
      // otherwise update end with current day
      else {
        last(rules)[1] = day;
      }

      return rules;
    }, [])
    // format rules
    .map(_formatOutletScheduleRule);
}

function _getDiffBetweenDays(aKey, bKey) {
  const aIndex = E_WEEK_DAY_KEYS_TO_INDEX_MAP[aKey];
  let bIndex = E_WEEK_DAY_KEYS_TO_INDEX_MAP[bKey];

  if (bIndex < aIndex) {
    bIndex += 7;
  }

  return bIndex - aIndex;
}

function _formatBakeryDeadlineRule(rule: any[]): IFormattedBakerySchedule {
  const begin = rule[0], end = rule[1];

  let formattedDaysName = '';
  let formattedOrderOnDay = '';

  if (!begin.hasPickupOrDelivery) {
    formattedDaysName = _getDayName(begin.targetKey);
  } else if (begin.targetKey === end.targetKey) {
    formattedOrderOnDay = begin.orderOnKey === null ? '' : _getDayName(begin.orderOnKey).toLowerCase();
    formattedDaysName = _getDayName(begin.targetKey).toLowerCase();
  } else {
    formattedDaysName = (_getDayName(begin.targetKey, 'ddd') + ' - ' + _getDayName(end.targetKey, 'ddd')).toLowerCase();
  }

  return {
    days: formattedDaysName,
    orderDaysBeforeCount: begin.daysDiff,
    orderOnDay: formattedOrderOnDay,
    deadline: dateHelper.stringifyMinutesCount(begin.orderBefore),
    hasPickupOrDelivery: begin.hasPickupOrDelivery
  };
}

function _formatOutletScheduleRule(rule: any[]): IFormattedOutletSchedule {
  const begin = rule[0],
    end = rule[1];

  let formattedDaysName = '';

  if (begin.targetKey === end.targetKey) {
    formattedDaysName = _getDayName(begin.targetKey);
  } else {
    formattedDaysName = _getDayName(begin.targetKey, 'ddd') + ' - ' + _getDayName(end.targetKey, 'ddd').toLowerCase();
  }

  let isClosed = begin.info.dayOff;
  let formattedBegin = isClosed ? '' : dateHelper.stringifyMinutesCount(begin.info.openingHoursStart);
  let formattedEnd = isClosed ? '' : dateHelper.stringifyMinutesCount(begin.info.openingHoursEnd);

  return {
    days: formattedDaysName,
    isClosed: begin.info.dayOff,
    begin: formattedBegin,
    end: formattedEnd
  };
}

function _getDayIndexToOrder(dayKey, schedule) {
  const daysBeforeOrder = schedule[dayKey].daysBeforeOrder;
  let dayIndex = E_WEEK_DAY_KEYS_TO_INDEX_MAP[dayKey] - daysBeforeOrder;

  if (dayIndex < 0) {
    dayIndex += 7;
  }

  return E_WEEK_DAY_KEYS_TO_INDEX_MAP[dayIndex];
}

function _getDayName(dayKey: number, format: string = 'dddd') {
  const dayIndex = E_WEEK_DAY_KEYS_TO_INDEX_MAP[dayKey];
  return dateHelper.getWeekDayName(dayIndex, format);
}

export const scheduleHelper = {
  getBakeryDeadlineRules,
  getOutletSchedule,
  getDeviatingDeadlines,
};
